import React, { useState, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Dropzone from 'react-dropzone';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@apollo/client';
import scrollTo from 'commons/scrollTo';
import { logError } from 'commons/logger';
import useUserData from 'data-hooks/useUserData';
import Checkbox from 'components/consumer/Checkbox';
import JBImage from 'components/essentials/JBImage';
import { SUBMIT_CUSTOMER_PHOTO } from 'mocks/mutations';
import StatusLabel from 'components/consumer/StatusLabel';
import CtaButton from 'components/essentials/Cta/CtaButton';
import withBrowserOnly from 'components/hoc/withBrowserOnly';
import ValidationError from 'components/consumer/ValidationError';
import LabeledTextarea from 'components/consumer/LabeledTextarea';
import FullScreenModal from 'components/consumer/FullScreenModal';
import DropzoneUploadLabel from 'components/consumer/Dropzone/DropzoneUploadLabel';
import DropzoneUploadWrapper from 'components/consumer/Dropzone/DropzoneUploadWrapper';
import SubmitPhotosUploaded from './components/SubmitPhotosUploaded';
const INITIAL_SUBMIT_PHOTOS = {
  imageFile: [],
  caption: '',
  consent: false
};
const content = `Joybird welcomes comments, feedback, reviews, notes, suggestions, thoughts, ideas, proposals, pictures, photographs, information, text, graphics, images, logos, illustrations, descriptions, data, designs, icons, video clips, audio clips, sounds, files, specifications, catalogs, literature, advertisements, titles, names and any other materials, but which shall expressly exclude any personally identifiable information (which use shall be governed by our Privacy Policy) (collectively, the “User-Generated Content”) from you on the Network, including but not limited to on its blog, product detail webpages and elsewhere. You agree that any User Generated Content that you post, upload, input, submit, send, contribute or otherwise transmit (collectively, “Submit” or “Submission”, as appropriate) to the Network (whether directly or through a third party) shall be true, accurate and complete and you shall not impersonate or falsely represent your affiliation with any person or entity. You are entirely responsible for all User Generated Content that you Submit or otherwise make available via the Network. \n \nBy Submitting User Generated Content to the Network, you grant, and you represent and warrant that you have the right to grant, to Joybird, its affiliates, agents and licensees, an irrevocable, perpetual, non-exclusive, fully paid, worldwide license to use, copy, perform, display, reproduce, publish, distribute, sublicense, and otherwise disclose, prepare derivative works of, or incorporate into other works, the User Generated Content, for any purpose without restriction and without providing any attribution or compensation to you. Such User Generated Content will not be treated as confidential information. In addition, by posting or allowing Joybird or a third party to post User Generated Content to any public area of the Network, you grant Joybird all rights necessary to prohibit any subsequent aggregation, display, copying, duplication, reproduction, or exploitation of the User Generated Content by any party for any purpose. \n \nYou agree that you shall not use the Network to Submit any User Generated Content that (i) is not related to appropriate subject matters, (ii) is misleading to others, (iii) is unlawful, harmful, threatening, abusive, harassing, tortuous, defamatory, vulgar, obscene, libelous, pornographic, indecent, invasive of another’s privacy, hateful, offensive, or racially, ethnically or otherwise objectionable, (iv) you do not have a right to Submit under any contractual relationships, (v) is infringing of any patent, trademark, trade secret, copyright or other proprietary rights of any third party, (vi) contains software viruses or any other computer code, files or programs designed to interrupt, destroy or limit the functionality of the Network, including but not limited to a virus, trojan horse, worm, or time bomb, or (vii) intentionally or unintentionally violates any applicable local, state, national or international law, or any rules or regulations having the force of law.\n \n In the event that you do not wish for Joybird to use your User Generated Content, please do not Submit such User Generated Content to the Network. Please note that any User Generated Content does not represent the advice, views, opinions or beliefs of Joybird, and Joybird makes no claim of accuracy of any such User Generated Content. You understand and agree that you are solely responsible for any User Generated Content that you Submit and you may be held legally liable for such User Generated Content. Joybird reserves the right to remove any User Generated Content from the Network at any time and for any reason.`;
const getBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
});
const submitPhotoFormSchema = Yup.object().shape({
  caption: Yup.string().required('Required'),
  imageFile: Yup.array().required('Required')
});
const SubmitPhotos = ({
  isOpen,
  onClose,
  product,
  onSubmit
}) => {
  const {
    isImpersonatingUser
  } = useUserData();
  const [uploaded, setUploaded] = useState(null);
  const [isPolicyOpen, setIsPolicyOpen] = useState(false);
  const [submitPhotoMutation] = useMutation(SUBMIT_CUSTOMER_PHOTO);
  const showPolicy = useCallback(() => {
    setIsPolicyOpen(true);
  }, [setIsPolicyOpen]);
  const resetUploaded = useCallback(() => {
    setUploaded(null);
  }, [setUploaded]);
  const renderForm = useCallback(() => {
    if (product?.photos?.length) {
      return <div>
          <div className="text-base mx-0 my-4 md:mb-12 submit-photos-desc-center">
            You have already submitted a photo for this product.
          </div>
          <div className="submit-photos-submitted-list">
            {product?.photos.map(photo => <div className="mb-8">
                <JBImage src={photo.url} sizes="75vw" />
                <div className="submit-photos-status">
                  <StatusLabel color={photo.isApproved ? 'brand' : 'gray'} className="submit-photos-status-label">
                    {photo.isApproved ? 'Approved' : 'In progress'}
                  </StatusLabel>
                </div>
              </div>)}
          </div>
        </div>;
    }
    return <Formik initialValues={INITIAL_SUBMIT_PHOTOS} validationSchema={submitPhotoFormSchema} onSubmit={async (values, actions) => {
      if (isImpersonatingUser) return;
      try {
        actions.setFieldError('formErrors', '');
        const formValues = {
          photo_comment: values.caption,
          image_name: values.imageFile[0].name,
          photo_image: await getBase64(values.imageFile[0]),
          cart_id: +product.cart_id,
          product_id: +product.product_id
        };
        const resp = await submitPhotoMutation({
          variables: {
            ...formValues
          }
        });
        actions.setSubmitting(false);
        const submitCustomerPhoto = resp?.data?.submitCustomerPhoto;
        const success = submitCustomerPhoto?.success ?? false;
        const message = submitCustomerPhoto?.message ?? 'Unable to upload product photo.';
        if (success) {
          scrollTo({
            left: 0,
            top: 0,
            behavior: 'smooth'
          });
          values.imageFile = [];
          values.caption = '';
          setUploaded(resp);
          onSubmit();
        } else {
          actions.setFieldError('formErrors', message);
          resetUploaded();
        }
      } catch (e) {
        actions.setSubmitting(false);
        actions.setFieldError('formErrors', e.message);
      }
    }}>
        {({
        isValid,
        dirty,
        values: values_0,
        errors,
        touched,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
        isSubmitting
      }) => <form onSubmit={handleSubmit}>
            <div>
              <div className="flex items-center justify-center mb-4">
                {!!product.image && <JBImage className="submit-photos__image" height={36} alt="product" trim="color" src={product.image} />}
              </div>

              <h2 className="text-2xl font-bold text-gray text-center mt-0 mx-0 mb-4 md:mt-8">
                Submit a photo
              </h2>

              <div className="text-base mx-0 my-4 md:mb-12">
                Snap a photo of your room with your Joybird furniture
                prominently featured (feel free to include family and pets!),
                upload it, and we’ll send your $25 gift card!
              </div>

              <div className="md:mx-auto md:my-0 md:max-w-[445px]">
                <div className="dropzone-wrapper">
                  <div className="text-center mt-2">
                    <DropzoneUploadWrapper allowUploads={values_0.imageFile.length === 0}>
                      <Dropzone accept="image/*" multiple={false} maxSize={2e7} name="submit-photo" onDrop={imageFiles => {
                    if (isImpersonatingUser) return;
                    setFieldValue('imageFile', imageFiles.map(i => Object.assign(i, {
                      preview: URL.createObjectURL(i)
                    })));
                  }} onFileDialogCancel={e_0 => {
                    logError(e_0);
                  }}>
                        {({
                      getRootProps,
                      getInputProps,
                      isDragActive
                    }) => <div {...getRootProps()} className={classNames({
                      'dropzone--isActive': isDragActive
                    })}>
                            {values_0.imageFile.length === 0 ? <>
                                <input autoCorrect="off" {...getInputProps()} />
                                <DropzoneUploadLabel allowMultiple={false} allowVideos={false} />
                              </> : values_0.imageFile.map(file => <Fragment key={file.preview}>
                                  <img src={file.preview} className="preview-image" alt={file.name} />
                                  <div className="image-details">
                                    <span>
                                      {file.name} (
                                      {new Intl.NumberFormat().format(file.size / 1024)}
                                      kB)
                                    </span>
                                    <button type="button" label="close-icon-button" className="close-icon" onClick={() => {
                            values_0.imageFile.forEach(item => URL.revokeObjectURL(item.preview));
                            setFieldValue('imageFile', []);
                          }} />
                                  </div>
                                </Fragment>)}
                          </div>}
                      </Dropzone>
                    </DropzoneUploadWrapper>
                  </div>
                </div>

                <div className="photo-upload-wrapper">
                  {uploaded?.data?.submitCustomerPhoto ? uploaded.data.submitCustomerPhoto.success === true ? <div className="success">
                        <span>Your photo has been uploaded. Thanks!</span>
                        <button type="button" onClick={resetUploaded} className="close-button">
                          X
                        </button>
                      </div> : <div className="failed">
                        <span>{uploaded.data.submitCustomerPhoto.message}</span>
                        <button type="button" onClick={resetUploaded} className="close-button">
                          X
                        </button>
                      </div> : null}
                </div>
                {touched.imageFile && errors.imageFile && <ValidationError data-xc="imageFile-validation">
                    {errors.imageFile}
                  </ValidationError>}

                <h4 className="text-base font-bold text-gray mt-6 mx-0 mb-0 md:mt-12">
                  Tell us how you like it, at least a few words!
                </h4>

                <LabeledTextarea label="Your thoughts" id="caption" name="caption" as="textarea" onChange={handleChange} value={values_0.caption} onBlur={handleBlur} readOnly={isImpersonatingUser} data-xc="caption-field" />

                {touched.caption && errors.caption && <ValidationError data-xc="caption-validation">
                    {errors.caption}
                  </ValidationError>}

                <div className="mt-6 md:mt-8">
                  <Checkbox name="consent" as="checkbox" checked={values_0.consent} onChange={handleChange} disabled={isImpersonatingUser} className="submit-photos-checkbox" />
                  <span className="text-base font-bold ml-6">
                    I have read and agreed to the{' '}
                    <button type="button" className="text-brand cursor-pointer" onClick={showPolicy}>
                      Photo Submission Policy.
                    </button>
                  </span>
                </div>

                <div className="mt-4">
                  <CtaButton type="submit" color="black" noLinkPadding hasArrow={false} styling="solid-button" spacing="sm" block data-xc="submit-photo-btn" disabled={isSubmitting || !values_0.consent || !(isValid && dirty) || isImpersonatingUser} textClassName="!lowercase" isLoading={isSubmitting}>
                    Submit
                  </CtaButton>

                  {!!errors.formErrors && <ValidationError data-xc="dsr-form-validation">
                      {errors.formErrors}
                    </ValidationError>}
                </div>
              </div>
            </div>
          </form>}
      </Formik>;
  }, [product, uploaded, onSubmit, showPolicy, setUploaded, resetUploaded, isImpersonatingUser, submitPhotoMutation]);
  return <>
      <FullScreenModal isOpen={isOpen} onClose={onClose} closeIcon="arrow">
        <div className="text-sm whitespace-pre-line text-gray md:mt-6 md:mx-auto md:mb-12 md:max-w-[700px]">
          {uploaded ? <SubmitPhotosUploaded onSubmitAnotherPhotoClick={onClose} /> : renderForm()}
        </div>
      </FullScreenModal>

      <FullScreenModal closeIcon="arrow" enableHashHistory={false} isOpen={isOpen && isPolicyOpen} onClose={() => {
      setIsPolicyOpen(false);
    }}>
        <div className="text-2xl font-bold text-gray text-center mt-0 mx-0 mb-4 md:mt-8">
          Our Photo Submission Policy
        </div>

        <div className="text-sm whitespace-pre-line text-gray md:mt-6 md:mx-auto md:mb-12 md:max-w-[700px]">
          {content}
        </div>
      </FullScreenModal>
    </>;
};
SubmitPhotos.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  product: PropTypes.shape({
    name: PropTypes.string,
    image: PropTypes.string,
    cart_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    product_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    photoStatus: PropTypes.oneOf(['In progress', 'Approved']),
    photos: PropTypes.arrayOf(PropTypes.shape({
      url: PropTypes.string,
      isApproved: PropTypes.bool
    }))
  })
};
export default withBrowserOnly(SubmitPhotos);